import {
  ProfileIcon,
  UserSquareIcon,
  Logout02Icon,
  HelpCircleIcon,
  ToggleOnIcon,
  TestTube01Icon,
  VolumeHighIcon,
} from "hugeicons-react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import "../../Styles/Settings/SettingsModal.css";
import "../../Styles/Layout/PageWrapper.css";
import { useEffect, useState } from "react";
import { useWindow } from "../../contexts/WindowContext";
import { chatConfig } from "../../config/chatConfig";
import { Section } from "./types";
import { ProfileSection } from "./ProfileSection";
import { AccountSection } from "./AccountSection";
import { PreferencesSection } from "./PreferencesSection";
import { BetaFeaturesSection } from "./BetaFeaturesSection";
import { SettingsSidebar } from "./SettingsSidebar";
import { VoicesSection } from "./VoicesSection";
import { Header } from "../Header/Header";

export const SettingsPage = () => {
  const { section } = useParams<{ section: string }>();
  const currentSection = section || 'profile';
  const { logout, user, isLoading: userLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { openConfirmationModal, confirmationModalState } = useWindow();

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Reset isLoggingOut when confirmation modal closes
  useEffect(() => {
    if (!confirmationModalState.isOpen && isLoggingOut) {
      setIsLoggingOut(false);
    }
  }, [confirmationModalState.isOpen, isLoggingOut]);

  const renderProfileSection = () => (
    <ProfileSection setIsLoading={setIsLoading} />
  );

  const renderAccountSection = () => (
    <AccountSection />
  );

  const renderPreferencesSection = () => (
    <PreferencesSection />
  );

  const renderBetaFeaturesSection = () => (
    <BetaFeaturesSection />
  );

  const renderVoicesSection = () => (
    <VoicesSection />
  );

  // Legacy URL from full model modal; model settings live in the chat picker only.
  useEffect(() => {
    if (section === 'model') {
      navigate('/settings/profile', { replace: true });
    }
  }, [section, navigate]);

  const handleLogout = () => {
    // Prevent multiple logout attempts
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    
    openConfirmationModal(
        "Are you sure you want to logout?",
        () => {
          logout();
          navigate('/');
        },
        null
      );
  };

  const sections: Section[] = [
    { id: 'profile', icon: UserSquareIcon, label: 'Profile', render: renderProfileSection },
    { id: 'account', icon: ProfileIcon, label: 'Account', render: renderAccountSection },
    { id: 'preferences', icon: ToggleOnIcon, label: 'Preferences', render: renderPreferencesSection },
    { id: 'beta', icon: TestTube01Icon, label: 'Beta Features', render: renderBetaFeaturesSection },
    { id: 'voices', icon: VolumeHighIcon, label: 'Voices', render: renderVoicesSection },
    {
      id: 'help',
      icon: HelpCircleIcon,
      label: 'Help',
      onClick: () => window.open(chatConfig.cognora.help_link, '_blank'),
    },
    {
      id: 'logout',
      icon: Logout02Icon,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  if (userLoading || !user) return null;
  
  return (
    <div className="page-wrapper">
      <Header isSettingsPage />
      <div className="settings-container">
        <div className="settings-container-main">
          <SettingsSidebar
            sections={sections}
            currentSection={currentSection}
            onSectionChange={(sectionId) => navigate(`/settings/${sectionId}`)}
          />
          <div 
            className="settings-container-main-content"
            role="tabpanel"
            id={`panel-${currentSection}`}
            aria-labelledby={currentSection}
          >
            {userLoading || isLoading ? (
              <div className="settings-container-main-content-loading">
                <div className="settings-container-main-content-loading-spinner" aria-label="Loading..." />
              </div>
            ) : (
              <div className="settings-content-wrapper">
                {sections.find((section) => section.id === currentSection)?.render?.()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};